import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "@mmfestival/shared-components";
import {
  collection,
  getDocs,
  writeBatch,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { 
  LuUsers, 
  LuTrophy, 
  LuUpload, 
  LuCalendar, 
  LuActivity, 
  LuChevronDown,
  LuTrash2,
  LuFileText,
  LuInfo,
  LuX
} from "react-icons/lu";
import Papa from "papaparse";

// Komponen StatsCard (Redesigned to match Dashboard)
const StatsCard = ({ title, value, icon, trend, subValue }) => (
   <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-start justify-between shadow-sm">
      <div>
         <p className="text-gray-500 text-[10px] font-semibold mb-1 uppercase tracking-wider">{title}</p>
         <h3 className="text-2xl font-bold text-slate-800 mb-1">{value}</h3>
         {trend && (
           <p className="text-[10px] text-slate-600 font-medium bg-slate-100 inline-block px-1.5 py-0.5 rounded">
              {trend}
           </p>
         )}
         {subValue && <p className="text-[10px] text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className="text-slate-400">
         {icon}
      </div>
   </div>
);

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const parseNameParts = (fullName = "") => {
  const sanitized = String(fullName).trim().replace(/\s+/g, " ");
  if (!sanitized) {
    return { firstName: "", familyName: "" };
  }

  const [firstName, ...rest] = sanitized.split(" ");
  return {
    firstName,
    familyName: rest.join(" "),
  };
};

const normalizeElapsedTime = (timeValue) => {
  if (!timeValue) return "";
  const normalized = String(timeValue).trim().replace(/\.(\d+)$/, "");
  return normalized;
};

const slugifyDistance = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getCanonicalSmmDistance = (categoryName, rawDistance) => {
  const categorySlug = slugifyDistance(categoryName);
  const distanceSlug = slugifyDistance(rawDistance);

  if (categorySlug === "7-km" || distanceSlug === "7") return "7";
  if (categorySlug === "half-marathon" || distanceSlug === "21" || distanceSlug === "21-9") return "21";
  if (categorySlug === "marathon" || distanceSlug === "42") return "42";
  if (categorySlug === "kaldera-kids-run") return "kaldera-kids-run";
  if (categorySlug === "porter-challenge") return "porter-challenge";

  return distanceSlug || categorySlug;
};

// Fungsi untuk mengonversi waktu HH:MM:SS ke detik untuk sorting numerik
const timeToSeconds = (time) => {
  if (!time) return Infinity;
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(9)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

// Komponen CategoryButton (Redesigned)
const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 shadow-sm border ${
      selectedCategory === category.distance
        ? "bg-slate-800 text-white border-slate-800 scale-105"
        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
    }`}
    aria-label={`Filter hasil berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

// Komponen untuk tabel top 3 (Redesigned)
const Top3Table = ({ title, results, isMixGroup }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col shadow-sm">
    <div className="p-4 border-b border-gray-100 bg-slate-50 flex items-center gap-2">
      <LuTrophy className="text-amber-500" size={18} />
      <h2 className="text-base font-bold text-slate-800">
        {title}
      </h2>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Pos
            </th>
            <th className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              {isMixGroup ? "Team" : "Name"}
            </th>
            <th className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Bib
            </th>
            <th className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
              Time
            </th>
            <th className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider text-right">
              Cat
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {results.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                className="px-4 py-8 text-center text-sm text-gray-400 italic"
              >
                No rankings available
              </td>
            </tr>
          ) : (
            results.map((result, index) => (
              <tr
                key={`top3-${result.id || index}`}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="px-4 py-3 text-sm font-bold text-slate-700">
                  #{result.genderRanking || result.ranking}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 font-medium whitespace-nowrap">
                  {isMixGroup
                    ? capitalizeWords(result.teamName) || "Unknown Team"
                    : capitalizeWords(
                        `${result.firstName} ${result.familyName || ""}`
                      ) || "-"}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                  {result.bibNumber || "-"}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-800">
                  {result.time || "-"}
                </td>
                <td className="px-4 py-3 text-[10px] text-gray-500 text-right font-medium">
                  {result.categoryName || "-"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export const RaceResultsUpload = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const isMounted = useRef(true);
  const [state, setState] = useState({
    raceResults: [],
    top3Male: [],
    top3Female: [],
    loading: false,
    totalResults: 0,
    selectedCategory: "",
    categories: [],
    categoriesLoaded: false,
    selectedDistance: null,
    bibNumberCache: new Map(),
    events: [],
    selectedEvent: "",
    loadingEvents: true,
  });
  // ... existing state ...
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvWarnings, setCsvWarnings] = useState([]);
  const [csvSuccessCount, setCsvSuccessCount] = useState(0);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [skipNameValidation, setSkipNameValidation] = useState(false);
  // ...
  const [clearExistingResults, setClearExistingResults] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ...

  // Fetch Events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, "semuaKegiatan");
        const snapshot = await getDocs(eventsRef);
        const eventsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        if (isMounted.current) {
          setState(prev => ({
            ...prev,
            events: eventsList,
            selectedEvent: eventsList.length > 0 ? eventsList[0].id : "",
            loadingEvents: false
          }));
        }
      } catch (err) {
        console.error("Failed to fetch events:", err);
        showNotification("error", "Gagal memuat daftar event.");
        if (isMounted.current) {
           setState(prev => ({ ...prev, loadingEvents: false }));
        }
      }
    };
    fetchEvents();
  }, [db, showNotification]);


  // ...

  const clearExistingRaceResults = async (distance) => {
     if (!state.selectedEvent) return;
    if (!distance) {
      showNotification("error", "Silakan pilih kategori sebelum menghapus.");
      return;
    }
    try {
      const subCollectionRef = collection(
        db,
        `results${state.selectedEvent}`, // Dynamic collection
        distance,
        "results"
      );
      // ... same logic ...
      const resultsSnapshot = await getDocs(subCollectionRef);
      const batch = writeBatch(db);
      resultsSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      showNotification(
        "success",
        `Berhasil menghapus hasil lama untuk kategori ${distance}.`
      );
    } catch (err) {
      console.error("[ERROR] Failed to clear existing results:", err);
      showNotification("error", "Gagal menghapus hasil lama.");
    }
  };

  const fetchAllBibNumbers = async () => {
    if (!state.selectedEvent) return new Map();
    try {
      const participantsCollection = collection(db, `pendaftaran${state.selectedEvent}`); // Dynamic collection
      const participantsSnapshot = await getDocs(participantsCollection);
      const bibNumberCache = new Map();
      participantsSnapshot.forEach((doc) => {
        const data = doc.data();
         // Logic might need adjustment depending on if categories names are consistent across events
         // accepting 'Mix Group (20K)' as generic for now or check data structure
        if (data.kategori && data.kategori.includes("Mix Group")) {
          const bibNumbers =
            data.participants?.map((p) => p.bibNumber || "-").join(", ") || "-";
          bibNumberCache.set(doc.id, {
            bibNumber: bibNumbers,
            teamName: capitalizeWords(data.teamName) || "-",
          });
        } else {
          bibNumberCache.set(doc.id, {
            bibNumber: data.bibNumber || "-",
            teamName: null,
          });
        }
      });
      return bibNumberCache;
    } catch (err) {
      console.error("[ERROR] Failed to fetch bibNumbers:", err);
      showNotification("error", "Gagal memuat nomor bib.");
      return new Map();
    }
  };

  const fetchCategories = useCallback(async () => {
    if (!state.selectedEvent) return;

    try {
      const eventRef = doc(db, "semuaKegiatan", state.selectedEvent);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];
        let mappedCategories = [];
        const seenDistances = new Set();

        rawCategories.forEach((cat) => {
          const rawDistance =
            cat.distance != null && String(cat.distance).trim() !== ""
              ? String(cat.distance).trim()
              : String(cat.categoryName || "").trim();

          let distance = slugifyDistance(rawDistance);

          if (state.selectedEvent === "SMM") {
            distance = getCanonicalSmmDistance(cat.categoryName, rawDistance);
          }

          let displayName = cat.categoryName;
          let categoryName = cat.categoryName;
          let description = cat.description || cat.categoryName;

          // Mapping logic can be generic or specific depending on needs. 
          // For now, attempting to keep existing mapping but generalized if possible.
          // If specific mapping is required per event, it might need more logic.

          // Fallback specific mappings if needed (legacy JOTR support)
          if (state.selectedEvent === 'JOTR') {
               if (cat.categoryName === "Vertical (7K)") {
                distance = "7-vertical";
              } else if (cat.categoryName === "Fun Trail (7K)") {
                distance = "7-fun";
              } else if (cat.categoryName === "Classic Distance (20K)") {
                distance = "20-classic";
              } else if (cat.categoryName === "Mix Group (20K)") {
                distance = "20-mix";
              } else if (cat.categoryName === "Short Distance (40K)") {
                 distance = "40";
              } else if (cat.categoryName === "Long Distance (80 K)") {
                  distance = "80";
              }
          }

          if (!seenDistances.has(distance)) {
            seenDistances.add(distance);
            mappedCategories.push({
              distance,
              displayName,
              description,
              categoryName,
            });
          }
        });

        // Add fallback for SMM if categories are empty
        const isSMM = state.selectedEvent && state.selectedEvent.toUpperCase().includes("SMM");
        if (isSMM && mappedCategories.length === 0) {
          mappedCategories = [
            {
              distance: "42",
              displayName: "SMM 42K",
              description: "SMM Marathon - 42K",
              categoryName: "Marathon",
            },
            {
              distance: "21",
              displayName: "SMM 21K",
              description: "SMM Half Marathon - 21K",
              categoryName: "Half Marathon",
            },
            {
              distance: "7",
              displayName: "SMM 7K",
              description: "SMM 7 KM",
              categoryName: "7 KM",
            },
          ];
        }

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: mappedCategories,
            categoriesLoaded: true,
            selectedCategory: mappedCategories.length > 0 
              ? (mappedCategories.find(c => c.distance === prev.selectedCategory)?.distance || mappedCategories[0].distance)
              : "",
          }));
        }
      } else {
        showNotification(
          "error",
          `Dokumen event ${state.selectedEvent} tidak ditemukan.`
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to fetch categories:", err);
      showNotification("error", "Gagal memuat kategori.");
    }
  }, [showNotification, state.selectedEvent]);

  // Load Race Data needs to depend on selectedEvent
   const loadRaceData = useCallback(
    async (selectedDistance) => {
       const distToLoad = selectedDistance || state.selectedCategory;
      if (!state.categoriesLoaded || state.categories.length === 0 || !state.selectedEvent) {
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const bibNumberCache = await fetchAllBibNumbers(); // Uses selectedEvent inside
        const subCollectionRef = collection(
          db,
          `results${state.selectedEvent}`, // Dynamic
          distToLoad,
          "results"
        );
        // ... rest of logic ...
        const resultsSnapshot = await getDocs(subCollectionRef);
        const category = state.categories.find(
          (cat) => cat.distance === distToLoad
        );

        if (!category) {
           setState((prev) => ({ ...prev, loading: false }));
           return;
        }

        let resultsList = resultsSnapshot.docs.map((doc) => {
           // ... same mapping ...
           const data = doc.data();
           const bibData = bibNumberCache.get(data.orderId) || {
            bibNumber: "-",
            teamName: data.teamName ? capitalizeWords(data.teamName) : null,
          };
          return {
            id: doc.id,
            ...data,
            categoryName: category.categoryName,
            bibNumber: bibData.bibNumber,
            teamName: bibData.teamName || data.teamName,
          };
        });

        // ... sorting logic ... 
        // Generalized Check for Mix Group or just rely on distance string if it contains 'mix'
         if (distToLoad.includes("mix")) {
           // ... mix logic
             resultsList = resultsList
            .filter((result) => result.time && result.ranking !== "DNF")
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              displayRanking: index + 1,
            }))
            .concat(
              resultsList
                .filter((result) => !result.time || result.ranking === "DNF")
                .map((result) => ({
                  ...result,
                  displayRanking: "DNF",
                }))
            );
         } else {
             // ... normal logic
             resultsList.sort((a, b) => {
                const rankA = a.ranking === "DNF" ? Infinity : parseInt(a.ranking) || Infinity;
                const rankB = b.ranking === "DNF" ? Infinity : parseInt(b.ranking) || Infinity;
                return rankA - rankB;
              });
               resultsList = resultsList.map((result) => ({
                ...result,
                displayRanking: result.ranking,
              }));
         }
        
         // Top 3 Logic
         let top3Male = [];
        let top3Female = [];

        if (distToLoad.includes("mix")) {
             top3Male = resultsList
            .filter((result) => result.ranking !== "DNF" && result.time)
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              ranking: index + 1,
            }));
        } else {
             top3Male = resultsList
             .filter(
              (result) =>
                (!result.gender || result.gender === "M") && result.ranking !== "DNF" && result.time
            )
             .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

             top3Female = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .slice(0, 3)
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));
        }

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            raceResults: resultsList,
            totalResults: resultsSnapshot.size,
            top3Male: top3Male,
            top3Female: distToLoad.includes("mix") ? [] : top3Female,
            loading: false,
            bibNumberCache,
          }));
        }

      } catch (err) {
        console.error("[ERROR] Error fetching race data:", err);
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loading: false }));
          showNotification(
            "error",
            "Gagal memuat hasil perlombaan."
          );
        }
      }
    }, [showNotification, state.categories, state.categoriesLoaded, state.selectedEvent]
   );

   // Update useEffect to trigger fetchCategories when selectedEvent changes
   useEffect(() => {
     if(state.selectedEvent) {
       fetchCategories();
     }
   }, [state.selectedEvent, fetchCategories]);

   // Update useEffect to load race data when category or event changes
   useEffect(() => {
       if(state.selectedEvent && state.selectedCategory) {
           loadRaceData(state.selectedCategory);
       }
   }, [state.selectedCategory, state.selectedEvent, loadRaceData]);

 // ... validate checks ...
 const findMatchingParticipant = async (
    firstName,
    gender,
    birthdate,
    distance,
    bibNumberCache
  ) => {
    if (!state.selectedEvent) return null;
    try {
      const participantsCollection = collection(db, `pendaftaran${state.selectedEvent}`); // Dynamic
      const participantsSnapshot = await getDocs(participantsCollection);

      // ... existing logic ...
       if (distance.includes("mix")) {
           // ... mix logic ...
             const matchingTeams = participantsSnapshot.docs.filter((doc) => {
             const data = doc.data();
             // Adjust category check to be more generic if needed
             if (
                data.transaction_status !== "settlement"
              ) {
                return false;
              }
              // Check if participant is in list
              const participants = data.participants || [];
               const isMatch = participants.some((participant) => {
                const namaDepanDb = normalizeString(participant.namaDepan || "");
                const namaDepanCsv = normalizeString(firstName);
                const genderDb = participant.gender === "Laki-laki" ? "M" : "F";
                 return (
                  (!skipNameValidation ? namaDepanDb === namaDepanCsv : true) &&
                  genderDb === gender &&
                  participant.tanggalLahir === birthdate
                );
              });
              return isMatch;
             });
             // ... return logic
              return matchingTeams.length === 1
          ? {
              doc: matchingTeams[0],
              teamName: capitalizeWords(matchingTeams[0].data().teamName),
              bibNumber:
                bibNumberCache.get(matchingTeams[0].id)?.bibNumber || "-",
            }
          : null;

       } else {
           // ... individual logic
           const matchingParticipants = participantsSnapshot.docs.filter((doc) => {
               const data = doc.data();
               const namaDepanDb = normalizeString(
                data.namaLengkap ? data.namaLengkap.split(" ")[0] : ""
              );
              const namaDepanCsv = normalizeString(firstName);
               const genderDb = data.gender === "Laki-laki" ? "M" : "F";
               return (
                data.transaction_status === "settlement" &&
                (!skipNameValidation ? namaDepanDb === namaDepanCsv : true) &&
                genderDb === gender &&
                data.tanggalLahir === birthdate
              );
           });
           // ... return logic
            return matchingParticipants.length === 1
          ? {
              doc: matchingParticipants[0],
              teamName: null,
              bibNumber:
                bibNumberCache.get(matchingParticipants[0].id)?.bibNumber ||
                "-",
            }
          : null;
       }

    } catch(err) {
        // ...
        return null;
    }
  };
  
  // handleCsvUpload ... no changes except ensure validations pass

  // handleProcessCsv ... ensure it uses selectedEvent for dynamic fetching of bibs etc.



  const normalizeString = (str) => {
    return str ? str.trim().replace(/\s+/g, " ").toLowerCase() : "";
  };

  const validateTimeFormat = (time) => {
    if (!time) return true;
    const regex = /^\d+:[0-5][0-9]:[0-5][0-9]$/;
    return regex.test(time);
  };

  const validateBirthdateFormat = (birthdate) => {
    if (!birthdate) return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    return regex.test(birthdate);
  };

  const validateGender = (gender) => {
    if (!gender) return false;
    const normalizedGender = normalizeString(gender);
    const validGenders = ["m", "f", "male", "female", "laki-laki", "perempuan"];
    return validGenders.includes(normalizedGender);
  };

  const normalizeGender = (gender) => {
    const normalized = normalizeString(gender);
    if (["m", "male", "laki-laki"].includes(normalized)) return "M";
    if (["f", "female", "perempuan"].includes(normalized)) return "F";
    return null;
  };

  const checkUserRole = async (user) => {
    if (!user) return false;
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) return false;
      const userRole = userDoc.data().role;
      return userRole === "admin" || userRole === "rpc";
    } catch (err) {
      console.error("[ERROR] Error checking user role:", err);
      return false;
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const hasPermission = await checkUserRole(authUser);
    if (!hasPermission) {
      showNotification(
        "error",
        "Anda tidak memiliki izin untuk mengunggah hasil perlombaan."
      );
      return;
    }

    setCsvFileName(file.name);
    setCsvErrors([]);
    setCsvWarnings([]);
    setCsvSuccessCount(0);
    setCsvData([]);
    setSkipNameValidation(false);
    setClearExistingResults(false);
    setState((prev) => ({
      ...prev,
      selectedDistance: prev.selectedCategory || null,
    }));

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const data = result.data;
        if (!data.length) {
          showNotification("error", "CSV kosong atau tidak valid.");
          return;
        }

        const legacyHeaders = [
          "Ranking",
          "Time",
          "Family Name",
          "First Name",
          "Gender",
          "Birthdate",
          "Nationality",
        ];

        const standingsHeaders = [
          "Rank",
          "NO BIB",
          "Nama Lengkap",
          "Gender",
          "Kategori",
          "Finish Time",
          "Total Time",
        ];

        const smmHeaders = [
          "Rank",
          "Bib",
          "Name",
          "Gender",
          "Category",
          "Finish",
        ];

        const headers = Object.keys(data[0]);
        const missingLegacyHeaders = legacyHeaders.filter(
          (header) => !headers.includes(header)
        );

        const missingStandingsHeaders = standingsHeaders.filter(
          (header) => !headers.includes(header)
        );

        const missingSmmHeaders = smmHeaders.filter(
          (header) => !headers.includes(header)
        );

        const isLegacyFormat = missingLegacyHeaders.length === 0;
        const isStandingsFormat = missingStandingsHeaders.length === 0;
        const isSmmFormat = missingSmmHeaders.length === 0;

        if (!isLegacyFormat && !isStandingsFormat && !isSmmFormat) {
          showNotification(
            "error",
            "Header CSV tidak sesuai format yang didukung. Gunakan format SMM (Rank, Bib, Name, Gender, Category, Finish) atau format standings/legacy."
          );
          return;
        }

        setCsvData(data);
        setIsCsvModalOpen(true);
      },
      error: (err) => {
        console.error("[ERROR] Error parsing CSV:", err);
        showNotification(
          "error",
          "Gagal memproses file CSV. Pastikan formatnya benar."
        );
      },
    });
  };

  const handleDistanceChange = (e) => {
    setState((prev) => ({ ...prev, selectedDistance: e.target.value }));
  };

  const handleProcessCsv = async () => {
    if (!csvData.length) {
      showNotification("error", "Tidak ada data CSV untuk diproses.");
      return;
    }
    if (!state.selectedDistance) {
      showNotification("error", "Silakan pilih kategori perlombaan.");
      return;
    }

    const hasPermission = await checkUserRole(authUser);
    if (!hasPermission) {
      showNotification(
        "error",
        "Anda tidak memiliki izin untuk memproses hasil perlombaan."
      );
      return;
    }

    setIsProcessingCsv(true);
    setCsvErrors([]);
    setCsvWarnings([]);
    setCsvSuccessCount(0);
    const newErrors = [];
    const newWarnings = [];
    const resultsToSave = [];
    const participantSet = new Set();
    const bibNumberCache = await fetchAllBibNumbers();

    if (clearExistingResults) {
      await clearExistingRaceResults(state.selectedDistance);
    }

    if (state.selectedDistance === "20-mix") {
      const hasTeamNameColumn = csvData.some((row) => row["Team Name"]);
      const teams = [];

      if (hasTeamNameColumn) {
        const teamMap = new Map();
        for (const row of csvData) {
          const {
            Ranking: ranking,
            Time: time,
            "Family Name": familyName,
            "First Name": firstName,
            Gender: gender,
            Birthdate: birthdate,
            Nationality: nationality,
            "Team Name": teamName,
          } = row;

          if (!ranking) {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Ranking tidak valid.`
            );
            continue;
          }
          const isDNF = ranking.toUpperCase() === "DNF";
          const parsedRanking = isDNF ? "DNF" : parseInt(ranking);
          if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Ranking harus berupa angka positif atau DNF.`
            );
            continue;
          }
          if (time && !validateTimeFormat(time)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Format durasi tidak valid (harus HH:MM:SS).`
            );
            continue;
          }
          if (!firstName || typeof firstName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
            );
            continue;
          }
          if (familyName && typeof familyName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
            );
            continue;
          }
          if (!validateGender(gender)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
            );
            continue;
          }
          if (!validateBirthdateFormat(birthdate)) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
            );
            continue;
          }
          if (!nationality || typeof nationality !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
            );
            continue;
          }
          if (!teamName || typeof teamName !== "string") {
            newErrors.push(
              `Baris ${csvData.indexOf(row) + 2}: Team Name tidak valid.`
            );
            continue;
          }

          const normalizedGender = normalizeGender(gender);
          if (!normalizedGender) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Gender "${gender}" tidak dapat dinormalisasi.`
            );
            continue;
          }

          if (!teamMap.has(teamName)) {
            teamMap.set(teamName, {
              ranking: parsedRanking,
              time: time || "",
              participants: [],
              orderId: null,
              teamName: capitalizeWords(teamName),
              categoryName: state.categories.find(
                (cat) => cat.distance === "20-mix"
              )?.categoryName,
              distance: "20-mix",
              createdAt: new Date(),
            });
          }
          teamMap.get(teamName).participants.push({
            firstName: capitalizeWords(firstName),
            familyName: capitalizeWords(familyName) || "",
            gender: normalizedGender,
            birthdate,
            nationality,
          });
        }

        for (const [teamName, team] of teamMap) {
          if (team.participants.length !== 3) {
            newErrors.push(
              `Tim "${teamName}" memiliki ${team.participants.length} anggota, harus 3 anggota.`
            );
            continue;
          }
          teams.push(team);
        }
      } else {
        for (let i = 0; i < csvData.length; i += 3) {
          const teamRows = csvData.slice(i, i + 3);
          if (teamRows.length < 3) {
            newErrors.push(
              `Baris ${i + 2} hingga ${
                i + teamRows.length + 1
              }: Tim tidak lengkap, hanya ada ${teamRows.length} anggota.`
            );
            continue;
          }

          const ranking = teamRows[0]["Ranking"];
          const time = teamRows[0]["Time"];
          const isDNF = ranking?.toUpperCase() === "DNF";
          const parsedRanking = isDNF ? "DNF" : parseInt(ranking);

          if (!ranking) {
            newErrors.push(`Baris ${i + 2}: Ranking tidak valid.`);
            continue;
          }
          if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
            newErrors.push(
              `Baris ${i + 2}: Ranking harus berupa angka positif atau DNF.`
            );
            continue;
          }
          if (time && !validateTimeFormat(time)) {
            newErrors.push(
              `Baris ${i + 2}: Format durasi tidak valid (harus HH:MM:SS).`
            );
            continue;
          }

          const participants = [];
          for (const row of teamRows) {
            const {
              "Family Name": familyName,
              "First Name": firstName,
              Gender: gender,
              Birthdate: birthdate,
              Nationality: nationality,
            } = row;

            if (!firstName || typeof firstName !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
              );
              continue;
            }
            if (familyName && typeof familyName !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
              );
              continue;
            }
            if (!validateGender(gender)) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
              );
              continue;
            }
            if (!validateBirthdateFormat(birthdate)) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
              );
              continue;
            }
            if (!nationality || typeof nationality !== "string") {
              newErrors.push(
                `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
              );
              continue;
            }

            const normalizedGender = normalizeGender(gender);
            if (!normalizedGender) {
              newErrors.push(
                `Baris ${
                  csvData.indexOf(row) + 2
                }: Gender "${gender}" tidak dapat dinormalisasi.`
              );
              continue;
            }

            participants.push({
              firstName: capitalizeWords(firstName),
              familyName: capitalizeWords(familyName) || "",
              gender: normalizedGender,
              birthdate,
              nationality,
            });
          }

          if (participants.length === 3) {
            teams.push({
              ranking: parsedRanking,
              time: time || "",
              participants,
              orderId: null,
              teamName: `Tim Ranking ${parsedRanking}`,
              categoryName: state.categories.find(
                (cat) => cat.distance === "20-mix"
              )?.categoryName,
              distance: "20-mix",
              createdAt: new Date(),
            });
          }
        }
      }

      // Urutkan tim berdasarkan waktu untuk menetapkan ranking berurutan
      const sortedTeams = teams
        .filter((team) => team.time && team.ranking !== "DNF")
        .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
        .map((team, index) => ({
          ...team,
          ranking: index + 1, // Perbarui ranking berdasarkan urutan waktu
        }))
        .concat(
          teams
            .filter((team) => !team.time || team.ranking === "DNF")
            .map((team) => ({
              ...team,
              ranking: "DNF",
            }))
        );

      for (const team of sortedTeams) {
        let matchedTeam = null;
        for (const participant of team.participants) {
          const participantMatch = await findMatchingParticipant(
            participant.firstName,
            participant.gender,
            participant.birthdate,
            "20-mix",
            bibNumberCache
          );
          if (participantMatch) {
            matchedTeam = participantMatch;
            break;
          }
        }

        if (matchedTeam) {
          team.orderId = matchedTeam.doc.id;
          team.teamName = matchedTeam.teamName;
          team.bibNumber = matchedTeam.bibNumber;
        } else {
          newWarnings.push(
            `Tim "${team.teamName}" tidak ditemukan di pendaftaran${state.selectedEvent}. Disimpan sebagai UNKNOWN.`
          );
          team.orderId = "UNKNOWN";
          team.bibNumber = "-";
        }
        resultsToSave.push(team);
      }
    } else {
      for (const row of csvData) {
        const isSmmFormat = row.Bib !== undefined && row.Finish !== undefined && row.Name !== undefined;
        const isStandingsRow =
          row.Rank !== undefined ||
          row["NO BIB"] !== undefined ||
          row["Nama Lengkap"] !== undefined;

        const ranking = row.Ranking ?? row.Rank;
        const time = normalizeElapsedTime(row.Time ?? row["Total Time"] ?? row.Finish);
        const finishTime = normalizeElapsedTime(row["Finish Time"] ?? row.Finish);
        const gender = row.Gender;

        const { firstName: parsedFirstName, familyName: parsedFamilyName } =
          isSmmFormat
            ? parseNameParts(row.Name)
            : isStandingsRow
            ? parseNameParts(row["Nama Lengkap"])
            : {
                firstName: row["First Name"] || "",
                familyName: row["Family Name"] || "",
              };

        const firstName = parsedFirstName;
        const familyName = parsedFamilyName;
        const birthdate = (isStandingsRow || isSmmFormat) ? "" : row.Birthdate;
        const nationality = (isStandingsRow || isSmmFormat)
          ? "Indonesia"
          : row.Nationality || "Indonesia";
        const rawBib = row["NO BIB"] ?? row.Bib;
        const csvBibNumber = (rawBib !== undefined && rawBib !== null) ? String(rawBib).trim() : "";

        if (!ranking) {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Ranking tidak valid.`
          );
          continue;
        }
        const isDNF = ranking.toUpperCase() === "DNF";
        const parsedRanking = isDNF ? "DNF" : parseInt(ranking);
        if (!isDNF && (isNaN(parsedRanking) || parsedRanking <= 0)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Ranking harus berupa angka positif atau DNF.`
          );
          continue;
        }
        if (time && !validateTimeFormat(time)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Format durasi tidak valid (harus HH:MM:SS).`
          );
          continue;
        }
        if (!firstName || typeof firstName !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nama Depan tidak valid.`
          );
          continue;
        }
        if (familyName && typeof familyName !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nama Belakang tidak valid.`
          );
          continue;
        }
        if (!validateGender(gender)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Gender harus M, F, Male, Female, Laki-laki, atau Perempuan.`
          );
          continue;
        }
        if (!isStandingsRow && !validateBirthdateFormat(birthdate)) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Format tanggal lahir tidak valid (harus YYYY-MM-DD).`
          );
          continue;
        }
        if (!nationality || typeof nationality !== "string") {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Nationality tidak valid.`
          );
          continue;
        }

        const normalizedGender = normalizeGender(gender);
        if (!normalizedGender) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Gender "${gender}" tidak dapat dinormalisasi.`
          );
          continue;
        }

        const participantKey = `${firstName}-${familyName || ""}-${birthdate}`;
        if (participantSet.has(participantKey)) {
          newErrors.push(
            `Baris ${csvData.indexOf(row) + 2}: Peserta ${firstName} ${
              familyName || ""
            } dengan tanggal lahir ${birthdate} sudah ada dalam CSV.`
          );
          continue;
        }
        participantSet.add(participantKey);

        const participantMatch = await findMatchingParticipant(
          firstName,
          normalizedGender,
          birthdate,
          state.selectedDistance,
          bibNumberCache
        );
        let orderId = "UNKNOWN";
        let distance = state.selectedDistance;
        let participantCategoryName = null;
        let teamName = null;
        let bibNumber = csvBibNumber || "-";

        if ((isStandingsRow || isSmmFormat) && csvBibNumber) {
          const matchedByBib = Array.from(bibNumberCache.entries()).find(
            ([, value]) => String(value?.bibNumber || "").trim() === csvBibNumber
          );

          if (matchedByBib) {
            orderId = matchedByBib[0];
          } else {
            newWarnings.push(
              `Baris ${csvData.indexOf(row) + 2}: BIB ${csvBibNumber} tidak ditemukan di pendaftaran${state.selectedEvent}. Disimpan sebagai UNKNOWN.`
            );
          }
        } else if (participantMatch) {
          const participantData = participantMatch.doc.data();
          orderId = participantMatch.doc.id;
          teamName = participantMatch.teamName;
          bibNumber = participantMatch.bibNumber;
          participantCategoryName = participantData.kategori || null;

          if (!participantCategoryName) {
            newWarnings.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: categoryName tidak ditemukan untuk peserta ${firstName} ${
                familyName || ""
              }. Menggunakan kategori dari modal.`
            );
          }
        } else {
          newWarnings.push(
            `Baris ${csvData.indexOf(row) + 2}: Peserta ${firstName} ${
              familyName || ""
            } tidak ditemukan di pendaftaran${state.selectedEvent}. Disimpan sebagai UNKNOWN.`
          );
        }

        if (
          !distance ||
          typeof distance !== "string" ||
          !state.categories.some((cat) => cat.distance === distance)
        ) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Jarak ${distance} tidak valid atau tidak ditemukan di kategori.`
          );
          continue;
        }

        const category = state.categories.find(
          (cat) => cat.distance === distance
        );
        resultsToSave.push({
          orderId,
          ranking: parsedRanking,
          time: time || "",
          familyName: capitalizeWords(familyName) || "",
          firstName: capitalizeWords(firstName),
          gender: normalizedGender,
          birthdate,
          nationality,
          finishTime,
          distance,
          categoryName: category.categoryName,
          teamName,
          bibNumber,
          createdAt: new Date(),
        });
      }
    }

    const rankingByDistance = new Map();
    for (const result of resultsToSave) {
      if (result.ranking !== "DNF") {
        const key = `${result.distance}-${result.ranking}`;
        if (rankingByDistance.has(key)) {
          newErrors.push(
            `Ranking ${result.ranking} sudah digunakan untuk jarak ${result.distance}.`
          );
        } else {
          rankingByDistance.set(key, result.orderId);
        }
      }
    }

    if (newErrors.length === 0 && resultsToSave.length > 0) {
      try {
        const resultsByDistance = new Map();
        resultsToSave.forEach((result) => {
          if (!result.distance) {
            newErrors.push(
              `Data tidak valid: Jarak tidak didefinisikan untuk ${
                result.teamName || result.firstName
              }.`
            );
            return;
          }
          if (!resultsByDistance.has(result.distance)) {
            resultsByDistance.set(result.distance, []);
          }
          resultsByDistance.get(result.distance).push(result);
        });

        if (newErrors.length > 0) throw new Error("Invalid distances found");

        for (const [distance, results] of resultsByDistance) {
          const subCollectionRef = collection(
            db,
            `results${state.selectedEvent}`,
            distance,
            "results"
          );
          const existingResults = await getDocs(subCollectionRef);
          const existingParticipantKeys = new Set();
          const existingRankings = new Map();

          existingResults.forEach((doc) => {
            const data = doc.data();
            if (distance === "20-mix") {
              const key = data.teamName || `team-${doc.id}`;
              existingParticipantKeys.add(key);
            } else {
              const key = `${data.firstName}-${data.familyName || ""}-${
                data.birthdate
              }`;
              existingParticipantKeys.add(key);
            }
            if (data.ranking !== "DNF") {
              existingRankings.set(data.ranking, doc.id);
            }
          });

          for (const result of results) {
            const participantKey =
              distance === "20-mix"
                ? result.teamName || `team-${result.orderId}`
                : `${result.firstName}-${result.familyName || ""}-${
                    result.birthdate
                  }`;
            if (existingParticipantKeys.has(participantKey)) {
              newErrors.push(
                `${
                  distance === "20-mix" ? "Tim" : "Peserta"
                } "${participantKey}" sudah ada di subkoleksi ${
                  result.categoryName || distance
                }.`
              );
            }
          }
        }

        if (newErrors.length === 0) {
          const batch = writeBatch(db);
          resultsToSave.forEach((result) => {
            const resultRef = doc(
              collection(db, `results${state.selectedEvent}`, result.distance, "results")
            );
            batch.set(resultRef, result);
          });
          await batch.commit();
          setCsvSuccessCount(resultsToSave.length);
          setState((prev) => ({
            ...prev,
            raceResults: [...prev.raceResults, ...resultsToSave],
            totalResults: prev.totalResults + resultsToSave.length,
            bibNumberCache,
          }));
          showNotification(
            "success",
            `Berhasil menyimpan ${resultsToSave.length} hasil perlombaan.${
              newWarnings.length > 0
                ? ` Ada ${newWarnings.length} peringatan.`
                : ""
            }`
          );
          setIsCsvModalOpen(false);
          setCsvData([]);
          setCsvFileName("");
          loadRaceData(state.selectedCategory);
        }
      } catch (err) {
        console.error("[ERROR] Error saving results:", err);
        newErrors.push("Gagal menyimpan hasil perlombaan ke database.");
      }
    }

    setCsvErrors(newErrors);
    setCsvWarnings(newWarnings);
    setIsProcessingCsv(false);

    if (newErrors.length > 0 || newWarnings.length > 0) {
      const firstError = newErrors[0];
      const firstWarning = newWarnings[0];
      showNotification(
        "error",
        `Proses selesai. ${
          newErrors.length > 0
            ? `Ada ${newErrors.length} kesalahan kritis. `
            : ""
        }${
          newWarnings.length > 0 ? `Ada ${newWarnings.length} peringatan.` : ""
        }${firstError ? ` Contoh error: ${firstError}` : ""}${
          !firstError && firstWarning ? ` Contoh peringatan: ${firstWarning}` : ""
        }`
      );
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleCategoryChange = useCallback(
    debounce((distance) => {
      if (distance === state.selectedCategory) return;

      if (
        !state.categoriesLoaded ||
        !state.categories.some((cat) => cat.distance === distance)
      ) {
        console.warn(`Invalid or unloaded category: ${distance}`);
        showNotification("error", "Kategori belum dimuat atau tidak valid.");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        raceResults: [],
        totalResults: 0,
        loading: true,
        top3Male: [],
        top3Female: [],
      }));
      loadRaceData(distance);
    }, 300),
    [
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
      showNotification,
    ]
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (state.categoriesLoaded && state.categories.length > 0) {
      loadRaceData(state.selectedCategory);
    }
  }, [
    state.categoriesLoaded,
    state.categories,
    state.selectedCategory,
    loadRaceData,
  ]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Memeriksa autentikasi...</p>
      </div>
    );
  }

  const currentDate = new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date()).toUpperCase();

  return (
    <div className="space-y-4 pb-10">
      {/* Hero Section (Match Dashboard) */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuActivity size={100} />
         </div>
         <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
               <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider">
                  <LuCalendar size={12} /> {currentDate}
               </p>
               <h1 className="text-2xl font-bold mb-1">Race Results Management</h1>
               <p className="text-slate-500 text-xs max-w-xl">
                  Upload and manage race results per event. Best viewed with <strong>Settlement</strong> participants only.
               </p>
            </div>

            {state.loadingEvents ? (
              <div className="w-full md:w-64 h-10 bg-slate-100 animate-pulse rounded-lg"></div>
            ) : (
              <div className="w-full md:w-64">
                <div className="relative group">
                  <select
                    value={state.selectedEvent || ""}
                    onChange={(e) => {
                      const newEvent = e.target.value;
                      setState((prev) => ({
                        ...prev,
                        selectedEvent: newEvent,
                        raceResults: [],
                        top3Male: [],
                        top3Female: [],
                        categories: [],
                        categoriesLoaded: false,
                        loading: true,
                      }));
                    }}
                    className="w-full pl-3 pr-10 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-slate-100 focus:border-slate-400 appearance-none transition-all cursor-pointer shadow-sm"
                  >
                    <option value="" disabled>Select Event</option>
                    {state.events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title || event.id}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
                    <LuChevronDown size={14} />
                  </div>
                </div>
              </div>
            )}
         </div>
      </div>

      {!state.selectedEvent ? (
        <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-xl border border-slate-200 p-8 text-center mt-4">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-300">
            <LuUpload size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-800">Please Select an Event</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-xs">
            Use the dropdown in the header to select an event and start managing its race results.
          </p>
        </div>
      ) : (
        <>
          {/* Statistics Grid (Match Dashboard) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatsCard
              title="Total Results"
              value={state.totalResults.toLocaleString()}
              icon={<LuUsers size={20} />}
              trend="Across All Categories"
              subValue={`${state.selectedEvent} Competition Data`}
            />
            <StatsCard
              title="Active Categories"
              value={state.categories ? state.categories.length : 0}
              icon={<LuTrophy size={20} />}
              trend="Defined in Config"
            />
            <StatsCard
              title="Event Status"
              value={state.selectedEvent}
              icon={<LuActivity size={20} />}
              trend="Currently selected"
            />
          </div>

            {/* Category selection and Actions Bar */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 space-y-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-50 pb-3">
                <div className="flex items-center gap-2">
                  <LuTrophy className="text-slate-400" size={16} />
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-tight">Race Categories</h3>
                </div>
                {state.selectedCategory && (
                  <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-bold uppercase">
                    Selected: {state.selectedCategory}
                  </span>
                )}
              </div>
              
              <div className="flex flex-wrap gap-2">
                {state.categories.length === 0 && !state.loading && (
                  <span className="text-slate-400 text-xs italic">
                    No categories found for this event.
                  </span>
                )}
                {state.categories.map((cat) => (
                  <CategoryButton
                    key={cat.distance}
                    category={cat}
                    selectedCategory={state.selectedCategory}
                    onClick={(dist) => {
                      setState((prev) => ({ ...prev, selectedCategory: dist }));
                    }}
                  />
                ))}
              </div>

              <div className="pt-2 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-slate-50 mt-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-slate-50 rounded-md text-slate-400">
                    <LuInfo size={14} />
                  </div>
                  <p className="text-[11px] text-slate-500 max-w-md">
                    To upload results, ensure your CSV follows the standard format. You can clear category data before re-uploading.
                  </p>
                </div>
                
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <button
                    onClick={() => {
                      if (!state.selectedCategory) {
                        showNotification("error", "Select a category first");
                        return;
                      }
                      if (window.confirm(`Are you sure you want to delete ALL results for category: ${state.selectedCategory}?`)) {
                        clearExistingRaceResults(state.selectedCategory);
                      }
                    }}
                    className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-red-500 bg-white border border-red-200 hover:bg-red-50 rounded-lg transition-colors text-xs font-semibold"
                  >
                    <LuTrash2 size={14} /> Clear {state.selectedCategory} Data
                  </button>
                  
                  <label className="flex-1 md:flex-none cursor-pointer bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg transition-all font-semibold flex items-center justify-center gap-2 shadow-sm text-xs">
                    <LuUpload size={14} /> Upload CSV
                    <input
                      type="file"
                      className="hidden"
                      accept=".csv"
                      onChange={handleCsvUpload}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Top 3 Tables */}
            <div className="flex flex-col md:flex-row gap-4 mb-8 mt-4">
              <Top3Table
                title={
                  state.selectedCategory === "20-mix"
                    ? "Top 3 Tim"
                    : "Top 3 Pria"
                }
                results={state.top3Male}
                isMixGroup={state.selectedCategory === "20-mix"}
              />
              {state.selectedCategory !== "20-mix" && (
                <Top3Table title="Top 3 Wanita" results={state.top3Female} />
              )}
            </div>

            {/* Full Results Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mt-4">
              <div className="p-4 border-b border-slate-50 bg-slate-50 flex items-center gap-2">
                <LuFileText className="text-slate-400" size={16} />
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-tight">Full Category Results</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        Rank
                      </th>
                      <th className="px-6 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        Bib No
                      </th>
                      <th className="px-6 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                        Full Name / Team
                      </th>
                      <th className="px-6 py-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wider text-right">
                        Finish Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {state.loading ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-12 text-center text-slate-400"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
                            <p className="text-xs">Loading race data...</p>
                          </div>
                        </td>
                      </tr>
                    ) : state.raceResults.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-6 py-12 text-center text-slate-400 italic text-sm"
                        >
                          No results found for this category.
                        </td>
                      </tr>
                    ) : (
                      state.raceResults.map((result) => (
                        <tr key={result.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-bold text-slate-800">
                            #{result.displayRanking}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-500 font-mono">
                            {result.bibNumber}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600 font-medium whitespace-nowrap">
                            {result.teamName ||
                              capitalizeWords(result.firstName + " " + (result.familyName || ""))}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-slate-800 text-right">
                            {result.time || result.finishTime || "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

      {isCsvModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-slate-800 text-white rounded-lg">
                  <LuUpload size={16} />
                </div>
                <h2 className="text-base font-bold text-slate-800 uppercase tracking-tight">Process CSV Results</h2>
              </div>
              <button 
                onClick={() => {
                  setIsCsvModalOpen(false);
                  setCsvData([]);
                  setCsvFileName("");
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LuX size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-start gap-3">
                <div className="mt-0.5 text-slate-400">
                  <LuFileText size={18} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5">Selected File</p>
                  <p className="text-sm font-semibold text-slate-800 truncate max-w-[300px]">{csvFileName || "Unknown.csv"}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Detected {csvData.length} data rows for import.</p>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Select Result Category
                </label>
                <div className="relative">
                  <select
                    value={state.selectedDistance || ""}
                    onChange={handleDistanceChange}
                    className="w-full pl-3 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-100 focus:border-slate-800 appearance-none transition-all cursor-pointer shadow-sm"
                  >
                    <option value="" disabled>-- Select Kategori --</option>
                    {state.categories.map((cat) => (
                      <option key={cat.distance} value={cat.distance}>
                        {cat.displayName}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
                    <LuChevronDown size={14} />
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-amber-300 text-slate-800 focus:ring-slate-800 cursor-pointer"
                      checked={clearExistingResults}
                      onChange={(e) => setClearExistingResults(e.target.checked)}
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-amber-800 group-hover:text-amber-900 transition-colors">Clear existing data</span>
                    <span className="text-[10px] text-amber-600">Remove all current results for this category before saving.</span>
                  </div>
                </label>
              </div>

              {(csvErrors.length > 0 || csvWarnings.length > 0) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-100 overflow-hidden">
                  <div className="px-4 py-2 bg-slate-100/50 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Validation Feedback</span>
                    <div className="flex gap-2">
                      {csvErrors.length > 0 && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">{csvErrors.length} Errors</span>}
                      {csvWarnings.length > 0 && <span className="text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded font-bold">{csvWarnings.length} Warnings</span>}
                    </div>
                  </div>
                  <div className="p-3 max-h-40 overflow-y-auto space-y-3">
                    {csvErrors.length > 0 && (
                      <ul className="space-y-1 text-[11px] text-red-600">
                        {csvErrors.slice(0, 10).map((err, index) => (
                          <li key={`csv-err-${index}`} className="flex gap-2 font-medium">
                            <span className="opacity-50">•</span> {err}
                          </li>
                        ))}
                      </ul>
                    )}
                    {csvWarnings.length > 0 && (
                      <ul className="space-y-1 text-[11px] text-amber-600">
                        {csvWarnings.slice(0, 10).map((warning, index) => (
                          <li key={`csv-warn-${index}`} className="flex gap-2 font-medium">
                            <span className="opacity-50">•</span> {warning}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsCsvModalOpen(false);
                  setCsvData([]);
                  setCsvFileName("");
                }}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-800 transition-all"
                disabled={isProcessingCsv}
              >
                Discard
              </button>
              <button
                onClick={handleProcessCsv}
                className="px-6 py-2 rounded-xl text-xs font-bold bg-slate-800 text-white hover:bg-slate-900 shadow-lg shadow-slate-200 disabled:opacity-50 disabled:shadow-none transition-all flex items-center gap-2"
                disabled={isProcessingCsv}
              >
                {isProcessingCsv ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <LuUpload size={14} /> Import & Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
